import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

export interface Technology {
  id: string;
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface TechnologyCarouselProps {
  technologies: Technology[];
  autoPlayInterval?: number;
}

export default function TechnologyCarousel({ 
  technologies, 
  autoPlayInterval = 5000 
}: TechnologyCarouselProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setInterval(() => {
      nextSlide();
    }, autoPlayInterval);

    return () => clearInterval(timer);
  }, [currentIndex, autoPlayInterval]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 300 : -300,
      opacity: 0
    })
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentIndex((prevIndex) => {
      let nextIndex = prevIndex + newDirection;
      if (nextIndex >= technologies.length) nextIndex = 0;
      if (nextIndex < 0) nextIndex = technologies.length - 1;
      return nextIndex;
    });
  };

  const nextSlide = () => paginate(1);
  const previousSlide = () => paginate(-1);

  return (
    <div className="relative w-full max-w-[300px] sm:max-w-xl md:max-w-2xl lg:max-w-4xl mx-auto px-2">
      <div className="relative h-[280px] overflow-hidden bg-base-200 rounded-box">
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={currentIndex}
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "spring", stiffness: 300, damping: 30 },
              opacity: { duration: 0.2 }
            }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(_, { offset, velocity }) => {
              const swipe = swipePower(offset.x, velocity.x);

              if (swipe < -swipeConfidenceThreshold) {
                paginate(1);
              } else if (swipe > swipeConfidenceThreshold) {
                paginate(-1);
              }
            }}
            className="absolute w-full h-full flex items-center justify-center"
          >
            <div className="flex flex-col items-center justify-center text-center p-4">
              <div className="text-4xl sm:text-5xl md:text-6xl text-primary mb-4 sm:mb-6">
                {technologies[currentIndex].icon}
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-2 sm:mb-4">
                {technologies[currentIndex].title}
              </h3>
              <p className="text-sm sm:text-base text-base-content/80 max-w-[260px] sm:max-w-md md:max-w-lg">
                {technologies[currentIndex].description}
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center mt-4">
        <button
          className="btn btn-circle btn-sm sm:btn-md btn-ghost"
          onClick={previousSlide}
        >
          <FiChevronLeft className="text-lg sm:text-2xl" />
        </button>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-1 sm:gap-2">
          {technologies.map((_, index) => (
            <button
              key={index}
              className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-colors ${
                index === currentIndex ? 'bg-primary' : 'bg-base-content/20'
              }`}
              onClick={() => {
                setDirection(index > currentIndex ? 1 : -1);
                setCurrentIndex(index);
              }}
            />
          ))}
        </div>

        <button
          className="btn btn-circle btn-sm sm:btn-md btn-ghost"
          onClick={nextSlide}
        >
          <FiChevronRight className="text-lg sm:text-2xl" />
        </button>
      </div>
    </div>
  );
}
